import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField } from "@mui/material";
import { Controller, useWatch } from "react-hook-form";
import "./style.css";
import libForm from "../../../util/libForm";
import { useContext, useRef, useState } from "react";
import { ContextGlobal } from "../../../context";
import { toast } from "react-toastify";
import LoadingFull from "../../loading/loading-full";
import CurrencyInput from "../../inputs/currency";
import { CreditCard, Banknote } from "lucide-react";

const TableInstallments = ({ installments, control, setValue, valor_total }) => {
	const { formPaymentData, bankData } = useContext(ContextGlobal);
	const [lastChangedIndex, setLastChangedIndex] = useState(null);
	const [loading, setLoading] = useState(false);
	const isProgrammatic = useRef(false);

	// Vigia todas as parcelas para reagir a mudanças de forma de pagamento
	const watchedParcelas = useWatch({
		control,
		name: "parcelas",
	});

	const handleFieldChange = (index, field, value) => {
		setValue(`parcelas.${index}.${field}`, value, { shouldValidate: true });

		if (field === "id_forma") {
			const idForma = Number(value);
			// Dinheiro (1), Pix (3), Débito (4) ou Crédito (5) nascem "Pago" (ativo: false) e data de vencimento hoje
			// Vale (2) e outros nascem "Pendente" (ativo: true) e data de vencimento para +30 dias
			if ([1, 3, 4, 5].includes(idForma)) {
				setValue(`parcelas.${index}.data_vencimento`, new Date().toISOString().split("T")[0], {
					shouldValidate: true,
				});
				setValue(`parcelas.${index}.ativo`, false, { shouldValidate: true });
			} else {
				const dateTarget = new Date();
				dateTarget.setDate(dateTarget.getDate() + 30);
				setValue(`parcelas.${index}.data_vencimento`, dateTarget.toISOString().split("T")[0], {
					shouldValidate: true,
				});
				setValue(`parcelas.${index}.ativo`, true, { shouldValidate: true });
			}
		}

		if (!isProgrammatic.current && field === "valor") {
			setLastChangedIndex(index);
		}
	};

	// Dinheiro é a única forma que pode ser alternada manualmente entre Pago/Pendente (mesmo
	// padrão do close-sale-modal) — ao alternar, a data acompanha: Pendente pula pra +30 dias,
	// Pago volta pra hoje.
	const handleToggleAtivo = (index, currentAtivo, idForma) => {
		const nextAtivo = !currentAtivo;
		handleFieldChange(index, "ativo", nextAtivo);

		if (Number(idForma) === 1) {
			const dateTarget = new Date();
			if (nextAtivo) dateTarget.setDate(dateTarget.getDate() + 30);
			setValue(`parcelas.${index}.data_vencimento`, dateTarget.toISOString().split("T")[0], {
				shouldValidate: true,
			});
		}
	};

	const parseValor = (val) => Math.round(parseFloat(val || 0) * 100); // retorna centavos como inteiro

	const corrigirParcela = (index, diffCentavos, mensagem) => {
		const atualCentavos = parseValor(installments[index].valor);
		const novoCentavos = atualCentavos + diffCentavos;

		setValue(`parcelas.${index}.valor`, (novoCentavos / 100).toFixed(2), {
			shouldValidate: true,
		});
		toast.error(mensagem);
	};

	const handleValorBlur = async () => {
		if (lastChangedIndex == null || lastChangedIndex < 0) return;

		try {
			setLoading(true);

			const somaAteAlterada = installments
				.slice(0, lastChangedIndex + 1)
				.reduce((acc, p) => acc + parseValor(p.valor), 0); // centavos até a parcela alterada

			const startIndex = lastChangedIndex + 1;
			const parcelasRestantes = installments.length - startIndex;
			const totalCentavos = Math.round(valor_total * 100);
			const diffFaltante = totalCentavos - somaAteAlterada;

			isProgrammatic.current = true;

			if (diffFaltante < 0) {
				// Valor excedido: corrige na própria parcela
				const totalAtual = installments.reduce((acc, p) => acc + parseValor(p.valor), 0);
				corrigirParcela(
					lastChangedIndex,
					totalCentavos - totalAtual,
					"Valor excede o total. Diferença corrigida na parcela alterada.",
				);
			} else if (parcelasRestantes > 0) {
				// Rateia restante entre as próximas parcelas
				const valorBase = Math.floor(diffFaltante / parcelasRestantes); // base por parcela
				let somaRateada = 0;

				for (let i = startIndex; i < installments.length - 1; i++) {
					setValue(`parcelas.${i}.valor`, (valorBase / 100).toFixed(2), { shouldValidate: true });
					somaRateada += valorBase;
				}

				// Última parcela recebe o restante para fechar 100%
				const ultimaParcelaIndex = installments.length - 1;
				const valorFinal = diffFaltante - somaRateada;
				setValue(`parcelas.${ultimaParcelaIndex}.valor`, (valorFinal / 100).toFixed(2), {
					shouldValidate: true,
				});

				// Garantia: soma exata das parcelas
				const totalAtual = somaAteAlterada + somaRateada + valorFinal;
			} else {
				// Última parcela: ajusta diferença nela
				const atualCentavos = parseValor(installments[lastChangedIndex].valor);
				const totalAtual = installments.reduce((acc, p) => acc + parseValor(p.valor), 0);
				const diff = totalCentavos - totalAtual;
				const novo = atualCentavos + diff;

				if (atualCentavos !== novo) {
					setValue(`parcelas.${lastChangedIndex}.valor`, (novo / 100).toFixed(2), {
						shouldValidate: true,
					});
					toast.error(
						"Não há parcelas abaixo para ajustar a diferença. Valor corrigido na parcela alterada.",
					);
				}
			}
		} catch (error) {
			toast.error("Erro ao corrigir valores das parcelas.");
		} finally {
			isProgrammatic.current = false;
			setLoading(false);
			setLastChangedIndex(null);
		}
	};

	const cellStyle = { padding: "8px" };

	const hasAnyDateInput = installments.some((parcela, index) => {
		const currentIdForma = watchedParcelas?.[index]?.id_forma ?? parcela.id_forma;
		const currentAtivo = watchedParcelas?.[index]?.ativo ?? parcela.ativo;
		// Exibe a coluna de data se a forma for Vale (não incluída no array [1,3,4,5]) 
		// OU se for Dinheiro (1) e estiver Pendente (ativo = true)
		return ![1, 3, 4, 5].includes(Number(currentIdForma)) || (Number(currentIdForma) === 1 && !!currentAtivo);
	});

	return (
		<>
			{loading && <LoadingFull mensagem={"Recalculando Parcelas"} />}
			<TableContainer>
				<Table>
					<TableHead className="color-table">
						<TableRow sx={{ height: "48px" }}>
							<TableCell sx={{ ...cellStyle, color: "white" }}>Parcela</TableCell>
							<TableCell sx={{ ...cellStyle, color: "white" }}>Valor</TableCell>
							<TableCell sx={{ ...cellStyle, color: "white" }}>Forma de Pagamento</TableCell>
							{hasAnyDateInput && <TableCell sx={{ ...cellStyle, color: "white" }}>Data</TableCell>}
							<TableCell sx={{ ...cellStyle, color: "white", textAlign: "center" }}>Status</TableCell>
							<TableCell sx={{ ...cellStyle, color: "white" }}>Conta do pagamento</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{installments.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={hasAnyDateInput ? 6 : 5}
									align="center"
									sx={{ padding: "24px", color: "#6b7280" }}
								>
									Nenhuma parcela gerada ainda. Preencha os parâmetros acima e clique em "Gerar
									Parcelas".
								</TableCell>
							</TableRow>
						) : (
							installments.map((parcela, index) => {
							// Usa o valor em tempo real monitorado pelo useWatch
							const currentIdForma = watchedParcelas?.[index]?.id_forma ?? parcela.id_forma;
							const currentAtivo = watchedParcelas?.[index]?.ativo ?? parcela.ativo;
							const isAutoDateRow = [1, 3, 4, 5].includes(Number(currentIdForma));
							const isDinheiroRow = Number(currentIdForma) === 1;
							// Vale sempre mostra data manual; Dinheiro só mostra quando está Pendente
							// (mesmo padrão do close-sale-modal — Pix/Cartão nunca mostram)
							const showDatePicker = !isAutoDateRow || (isDinheiroRow && !!currentAtivo);

							return (
								<TableRow key={parcela.id}>
									<TableCell sx={cellStyle}>
										<span>{index + 1}</span>
									</TableCell>

									<TableCell sx={cellStyle}>
										<Controller
											name={`parcelas.${index}.valor`}
											control={control}
											defaultValue={parcela.valor}
											render={({ field, fieldState }) => (
												<CurrencyInput
													label=""
													name={`parcelas.${index}.valor`}
													control={control}
													errors={{ [`parcelas.${index}.valor`]: fieldState.error }}
													className="w-full"
													required
													onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
													onValueBlur={handleValorBlur}
													onValueChange={(newValue) =>
														handleFieldChange(index, "valor", parseFloat(newValue || 0))
													}
												/>
											)}
										/>
									</TableCell>

									<TableCell sx={cellStyle}>
										<Controller
											name={`parcelas.${index}.id_forma`}
											control={control}
											defaultValue={parcela.id_forma}
											render={({ field, fieldState }) => (
												<div className="content-select input-select-box d-flex flex-column min-w-52">
													<select
														{...field}
														onChange={(e) =>
															handleFieldChange(
																index,
																"id_forma",
																parseInt(e.target.value),
															)
														}
														onKeyDown={(e) => e.key === "Enter" && libForm.EnterTab(e)}
														className="custom-select"
													>
														{formPaymentData?.map((forma) => (
															<option key={forma.id_forma} value={forma.id_forma}>
																{forma.nome}
															</option>
														))}
													</select>
													{fieldState.error && (
														<p style={{ color: "red" }}>{fieldState.error.message}</p>
													)}
												</div>
											)}
										/>
									</TableCell>

									{hasAnyDateInput && (
										<TableCell sx={cellStyle}>
											{showDatePicker && (
												<Controller
													name={`parcelas.${index}.data_vencimento`}
													control={control}
													defaultValue={
														new Date(parcela.data_vencimento).toISOString().split("T")[0]
													}
													render={({ field, fieldState }) => (
														<TextField
															{...field}
															type="date"
															value={field.value}
															onChange={(e) =>
																handleFieldChange(
																	index,
																	"data_vencimento",
																	e.target.value,
																)
															}
															onClick={(e) => e.target.showPicker()}
															error={!!fieldState.error}
															helperText={fieldState.error?.message}
															onKeyDown={(e) => e.key === "Enter" && libForm.EnterTab(e)}
															sx={{
																width: "100%",
																height: "49px",
																"& .MuiOutlinedInput-root": {
																	"& fieldset": { borderWidth: "1px" },
																	"&:hover fieldset": { borderWidth: "1px" },
																	"&.Mui-focused fieldset": { borderWidth: "1px" },
																},
																"& .MuiInputBase-root": {
																	height: "43px",
																	fontSize: "0.875rem",
																},
															}}
														/>
													)}
												/>
											)}
										</TableCell>
									)}

									<TableCell sx={cellStyle}>
										<Controller
											name={`parcelas.${index}.ativo`}
											control={control}
											defaultValue={parcela.ativo}
											render={({ field, fieldState }) => {
												const idForma = Number(currentIdForma);
												const isCard = [4, 5].includes(idForma);
												const isPix = idForma === 3;

												// Se for Pix ou Cartão, o status é fixo (não clicável)
												if (isCard || isPix) {
													return (
														<div className="flex justify-center items-center">
															<div style={{ cursor: "default" }} title="Pago">
																{isCard ? (
																	<CreditCard
																		size={24}
																		color="#22c55e"
																		strokeWidth={2.5}
																	/>
																) : (
																	<Banknote
																		size={24}
																		color="#22c55e"
																		strokeWidth={2.5}
																	/>
																)}
															</div>
														</div>
													);
												}

												// Para as demais formas, o clique é permitido
												return (
													<div className="flex justify-center items-center">
														<div
															className={`${
																field.value
																	? "badge_status bg_red"
																	: "badge_status bg_green"
															}`}
															onClick={() =>
																handleToggleAtivo(index, field.value, currentIdForma)
															}
															style={{ cursor: "pointer" }}
														>
															{field.value ? "Pendente" : "Pago"}
														</div>
														{fieldState.error && (
															<p style={{ color: "red", fontSize: "10px" }}>
																{fieldState.error.message}
															</p>
														)}
													</div>
												);
											}}
										/>
									</TableCell>

									<TableCell sx={cellStyle}>
										<Controller
											name={`parcelas.${index}.id_conta`}
											control={control}
											defaultValue={parcela.id_conta}
											render={({ field, fieldState }) => (
												<div className="content-select input-select-box d-flex flex-column min-w-52">
													<select
														{...field}
														onChange={(e) =>
															handleFieldChange(
																index,
																"id_conta",
																parseInt(e.target.value),
															)
														}
														onKeyDown={(e) => e.key === "Enter" && libForm.EnterTab(e)}
														className="custom-select"
													>
														{bankData?.map((bank) => (
															<option key={bank.id_conta} value={bank.id_conta}>
																{bank.banco}
															</option>
														))}
													</select>
													{fieldState.error && (
														<p style={{ color: "red" }}>{fieldState.error.message}</p>
													)}
												</div>
											)}
										/>
									</TableCell>
								</TableRow>
							);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

export default TableInstallments;
